import { PureComponent } from "react"
import ReactDOM from "react-dom"
import { Query, Mutation } from "react-apollo"
import gql from "graphql-tag"
import Router from "next/router"
import Composer from "react-composer"
import has from "lodash.has"

import UserTile from "../../components/user-tile/user-tile"
import UserTilePromo from "../../components/user-tile/user-tile-promo"
import Pagination from "../../components/pagination/pagination"
import AdditionalDetails from "./additional-details"
import { perPage, perPagePremium } from "../../config"
import PagePopup from "../../components/page-popup/page-popup"
import UserGridFilters from "./user-grid-filters"
import capitalize from "../../utils/capitalize"
import Cta from "../../components/cta/cta"

import { industriesData } from "../../data/industries.json"
import { ideaStagesData } from "../../data/idea-stages.json"
import { hoursPerWeekData } from "../../data/hours-per-week.json"
import { skillsData } from "../../data/skills.json"

import { LOCAL_UI_QUERY } from "../../apollo-state/graphql/queries"
import {
  LOCAL_TOGGLE_FOUNDERS_GRID_ADDITIONAL_DETAILS_MUTATION,
  LOCAL_TOGGLE_PREMIUM_COFOUNDER,
} from "../../apollo-state/graphql/mutations"

import "./user-grid.scss"
import VendorTile from "../../components/vendor-tile/vendor-tile"

const USERS_LOOKING_FOR_COFOUNDER = gql`
  query USERS_LOOKING_FOR_COFOUNDER(
    $skip: Int = 0
    $perPage: Int
    $where: String
    $sortByLastActive: Boolean
    $myId: ID
  ) {
    usersLookingForCofounder(
      skip: $skip
      perPage: $perPage
      where: $where
      sortByLastActive: $sortByLastActive
    ) {
      id
      firstName
      profilePic
      location
      role
      hasValidCoFounderSubscription
      whyMakeGoodCofounder
      lookingToJoinStartupAsFounder
      hasIdeaForStartup
      elevatorPitchForStartup
      knowledgePoints
      activityStatus
      lastActive
      ideas{
      title
      id
      elevatorPitch
      totalLikes
      totalComments
      totalSaves
      isActive
      ideaIndustry {
        id
        itemId
        label
      }
      ideaStage {
        id
        itemId
        label
      }
      saves {
        id
        user {
          id
        }
      }
      likes {
        id
        user {
          id
        }
        } 
      }
      addedBy(where: { id: $myId }) {
        id
      }
      hoursPerWeekAvailable {
        id
        label
      }
      ideaStage {
        id
        label
      }
      ideaIndustry {
        id
        label
      }
      hasSkills {
        id
        label
      }
      wantsSkills {
        id
        label
      }
    }
  }
`

const PREMIUM_USERS_LOOKING_FOR_COFOUNDER = gql`
  query PREMIUM_USERS_LOOKING_FOR_COFOUNDER(
    $skipPremium: Int = 0
    $perPagePremium: Int
    $where: String
    $myId: ID
  ) {
    premiumUsersLookingForCofounder(
      skip: $skipPremium
      perPagePremium: $perPagePremium
      where: $where
    ) {
      id
      firstName
      profilePic
      location
      role
      hasValidCoFounderSubscription
      whyMakeGoodCofounder
      lookingToJoinStartupAsFounder
      hasIdeaForStartup
      elevatorPitchForStartup
      knowledgePoints
      activityStatus
      lastActive
      addedBy(where: { id: $myId }) {
        id
      }
      hoursPerWeekAvailable {
        id
        label
      }
      ideaStage {
        id
        label
      }
      ideaIndustry {
        id
        label
      }
      hasSkills {
        id
        label
      }
      wantsSkills {
        id
        label
      }
    }
  }
`
const USERS_LOOKING_FOR_Vendor = gql`
  query USERS_LOOKING_FOR_Vendor(
    $skip: Int = 0
    $perPage: Int
    $where: String
    $sortByLastActive: Boolean
    $myId: ID
  ) {
    usersLookingForVendor(
      skip: $skip
      perPage: $perPage
      where: $where
      sortByLastActive: $sortByLastActive
    ) {
      id
      firstName
      companyName
      profilePic
      role
      shortDescription
      longDescription
      vendorUrl
      location
      hasValidCoFounderSubscription
      whyMakeGoodCofounder
      lookingToJoinStartupAsFounder
      hasIdeaForStartup
      elevatorPitchForStartup
      knowledgePoints
      activityStatus
      lastActive
      addedBy(where: { id: $myId }) {
        id
      }
      hoursPerWeekAvailable {
        id
        label
      }
      ideaStage {
        id
        label
      }
      ideaIndustry {
        id
        label
      }
      capability {
        id
        label
      }
      hasSkills {
        id
        label
      }
      wantsSkills {
        id
        label
      }
    }
  }
`
const portalNode = process.browser
  ? document.getElementById("page-portal")
  : null

const mappedWantsSkills = skillsData
  .filter((skill) => skill.itemId !== 0)
  .map((data) => data.itemId)
const mappedIdeaStages = ideaStagesData
  .filter((ideaStage) => ideaStage.itemId !== 0)
  .map((data) => data.itemId)
const mappedHoursPerWeekData = hoursPerWeekData
  .filter((hourPerWeek) => hourPerWeek.itemId !== 0)
  .map((data) => data.itemId)
const mappedIndustriesData = industriesData
  .filter((industry) => industry.itemId !== 0)
  .map((data) => data.itemId)
const mappedContacts = 1

function hasQueryParams(obj) {
  if (!obj) return false
  return Object.keys(obj).length > 0
}

function convertQueryToNonApplied(queryObj) {
  const newObj = {}
  Object.keys(queryObj).forEach((key) => {
    newObj[`${key.replace("AF", "F")}`] = queryObj[key]
  })
  return newObj
}

function encodeAFValues(filterValues) {
  const queryObj = {}
  Object.keys(filterValues).forEach((key) => {
    queryObj[key] = JSON.stringify(filterValues[key])
  })
  return queryObj
}

function decodeAFValues(queryObject, defaultFilterState) {
  const filterValues = {}

  Object.keys(queryObject).forEach((key) => {
    if (key in defaultFilterState) {
      filterValues[key] = JSON.parse(decodeURIComponent(queryObject[key]))
    }
  })
  return filterValues
}

function queryWithDiffFromDefault(newQuery, defaultFilterState) {
  if (!newQuery) return {}
  Object.keys(newQuery).forEach((key) => {
    if (
      JSON.stringify(defaultFilterState[key]) === JSON.stringify(newQuery[key])
    ) {
      delete newQuery[key]
    }
  })
  return newQuery
}

function setInitialFilterState(defaultFilterState, query) {
  let parsedQuery
  let derivedStateFromQuery
  if (query) {
    parsedQuery = decodeAFValues(query, defaultFilterState)
    derivedStateFromQuery = convertQueryToNonApplied(parsedQuery)
  }

  return query ? derivedStateFromQuery : defaultFilterState
}

const createFakeUserArray = (perPagePremium, premiumUserArray) => {
  let premiumUsersFakeArray = []
  for (let i = 0; i < perPagePremium; i++) {
    premiumUsersFakeArray.push(i)
  }

  if (premiumUserArray.length < perPagePremium - 1) {
    premiumUsersFakeArray.pop()
  }
  return premiumUsersFakeArray
}

class UserGrid extends PureComponent {
  constructor(props) {
    super(props)

    const defaultFilterState = {
      FHasIdeaForStartup: true,
      FLookingToJoinStartupAsFounder: true,
      FHasSkills: mappedWantsSkills,
      FIdeaStages: mappedIdeaStages,
      FContacts: mappedContacts,
      FHoursPerWeekData: mappedHoursPerWeekData,
      FIndustriesData: mappedIndustriesData,
      FLocationByCity: false,
      FLocationByCountry: false,
      FLastActive: false,
      FKeywordSearch: "",
      AFHasIdeaForStartup: true,
      AFLookingToJoinStartupAsFounder: true,
      AFHasSkills: mappedWantsSkills,
      AFIdeaStages: mappedIdeaStages,
      AFContacts: mappedContacts,
      AFHoursPerWeekData: mappedHoursPerWeekData,
      AFIndustriesData: mappedIndustriesData,
      AFLocationByCity: false,
      AFLocationByCountry: false,
      AFLastActive: false,
      AFKeywordSearch: "",
      pendingFiltersToApply: {},
    }

    if (props.me) {
      const meMappedWantsSkills = props.me.wantsSkills.map(
        (data) => data.itemId
      )
      const hasSkillsSet = Object.keys(meMappedWantsSkills).length > 0

      defaultFilterState.AFHasIdeaForStartup =
        props.me.lookingToJoinStartupAsFounder
      defaultFilterState.AFLookingToJoinStartupAsFounder =
        props.me.hasIdeaForStartup
      defaultFilterState.AFHasSkills = hasSkillsSet
        ? meMappedWantsSkills
        : mappedWantsSkills
      defaultFilterState.FHasIdeaForStartup =
        props.me.lookingToJoinStartupAsFounder
      defaultFilterState.FLookingToJoinStartupAsFounder =
        props.me.hasIdeaForStartup
      defaultFilterState.FHasSkills = hasSkillsSet
        ? meMappedWantsSkills
        : mappedWantsSkills

      if (
        !props.me.lookingToJoinStartupAsFounder &&
        !props.me.hasIdeaForStartup
      ) {
        defaultFilterState.AFHasIdeaForStartup = true
        defaultFilterState.AFLookingToJoinStartupAsFounder = true
        defaultFilterState.FHasIdeaForStartup = true
        defaultFilterState.FLookingToJoinStartupAsFounder = true
      }
    }

    this.state = {
      ...defaultFilterState,
      usersPerPage: perPage,
      defaultFilterState,
    }
  }

  componentDidMount() {
    const filterInitState = setInitialFilterState(
      this.state.defaultFilterState,
      hasQueryParams(this.props.query) ? this.props.query : undefined
    )
    this.setState({
      ...filterInitState,
    })
  }

  componentDidUpdate(prevProps) {
    // incase of navigation between queries need to update the filter state
    if (JSON.stringify(prevProps.query) !== JSON.stringify(this.props.query)) {
      const parsedNewQuery = decodeAFValues(
        this.props.query,
        this.state.defaultFilterState
      )
      this.setState(convertQueryToNonApplied(parsedNewQuery))
    }
  }

  updateValue = ({ forLabel, value }) => {
    this.setState({
      [forLabel]: value,
    })
    this.setPendingFilter({
      forLabel,
      value,
    })
  }

  toggleSelection = ({ forLabel, id }) => {
    const copiedState = [...this.state[forLabel]]
    const index = copiedState.indexOf(id)

    if (index > -1) {
      copiedState.splice(index, 1)
    } else {
      copiedState.push(id)
    }
    this.updateValue({
      forLabel,
      value: copiedState,
    })
    this.setPendingFilter({
      forLabel,
      value: copiedState,
    })
  }

  setPendingFilter = ({ forLabel, value }) => {
    const pendingFiltersToApply = { ...this.state.pendingFiltersToApply }
    pendingFiltersToApply[`A${capitalize(forLabel)}`] = value
    this.setState({
      pendingFiltersToApply,
    })
  }

  applyFilters = () => {
    const parsedCurrentQuery = decodeAFValues(
      this.props.query,
      this.state.defaultFilterState
    )
    const newQuery = {
      ...parsedCurrentQuery,
      ...this.state.pendingFiltersToApply,
    }

    this.setState(
      {
        pendingFiltersToApply: {},
      },
      () => {
        Router.push({
          pathname: "/find-a-co-founder",
          query: {
            ...encodeAFValues(
              queryWithDiffFromDefault(newQuery, this.state.defaultFilterState)
            ),
          },
        })
      }
    )
  }

  resetFilters = () => {
    const filterInitState = setInitialFilterState(
      this.state.defaultFilterState,
      undefined
    )
    this.setState(filterInitState, () => {
      Router.push({
        pathname: "/find-a-co-founder",
      })
    })
  }

  render() {
    const {
      FHasIdeaForStartup,
      FLookingToJoinStartupAsFounder,
      FHasSkills,
      FIdeaStages,
      FHoursPerWeekData,
      FIndustriesData,
      FLocationByCity,
      FLocationByCountry,
      FLastActive,
      FContacts,
      FKeywordSearch,
      usersPerPage,
      defaultFilterState,
    } = this.state

    const { pageNumber, me, signupOverlayActive, toggleFounderFilters, query } =
      this.props

    let copiedDefaultAFValues = { ...defaultFilterState }

    if (hasQueryParams(query)) {
      const decodedQuery = decodeAFValues(query, defaultFilterState)
      Object.keys(decodedQuery).forEach((key) => {
        if (copiedDefaultAFValues[key] !== undefined) {
          copiedDefaultAFValues[key] = decodedQuery[key]
        }
      })
    }

    let {
      AFHasIdeaForStartup,
      AFLookingToJoinStartupAsFounder,
      AFHasSkills,
      AFIdeaStages,
      AFHoursPerWeekData,
      AFIndustriesData,
      AFLocationByCity,
      AFLocationByCountry,
      AFLastActive,
      AFContacts,
      AFKeywordSearch,
    } = copiedDefaultAFValues

    const whereObject = {
      OR: [],
    }

    // first we loop over the filters that can be applied to logged out or logged in and apply to both hasIdea or lookingTo without idea and create an "extra" object to merge later on
    const orObjectGlobals = {}
    if (AFHasSkills.length !== skillsData.length) {
      orObjectGlobals.hasSkills_some = {
        OR: AFHasSkills.map((skill) => ({ itemId: skill })),
      }
    }
    if (AFHoursPerWeekData.length !== hoursPerWeekData.length - 1) {
      orObjectGlobals.hoursPerWeekAvailable = {
        OR: AFHoursPerWeekData.map((hourPerWeek) => ({
          itemId: hourPerWeek,
        })),
      }
    }

    // this query is for those who don't have ideas
    if (AFLookingToJoinStartupAsFounder) {
      whereObject.OR.push({
        lookingToJoinStartupAsFounder: true,
        ...orObjectGlobals,
      })
    }

    // this query is for those who have an idea for a startup
    if (AFHasIdeaForStartup) {
      const hasIdeaQueryObj = {
        hasIdeaForStartup: true,
        ...orObjectGlobals,
      }

      if (AFIdeaStages.length !== ideaStagesData.length - 1) {
        hasIdeaQueryObj.ideaStage = {
          OR: AFIdeaStages.map((ideaStage) => ({
            itemId: ideaStage,
          })),
        }
      }

      if (AFIndustriesData.length !== industriesData.length - 1) {
        hasIdeaQueryObj.ideaIndustry = {
          OR: AFIndustriesData.map((industry) => ({
            itemId: industry,
          })),
        }
      }
      whereObject.OR.push(hasIdeaQueryObj)
    }

    if (me) {
      whereObject.OR.forEach((_, idx) => {
        if (AFLocationByCity) whereObject.OR[idx].location = me.location
        if (AFLocationByCountry)
          whereObject.OR[idx].locationCountry = me.locationCountry
        if (AFContacts && AFContacts !== 1) {
          whereObject.OR[idx][
            `${AFContacts === 2 ? "addedBy_some" : "addedBy_none"}`
          ] = { id: me.id }
        }
        if (AFKeywordSearch.length > 0) {
          whereObject.OR[idx].OR = [
            {
              whyMakeGoodCofounder__lower_contains:
                AFKeywordSearch.trim().toLowerCase(),
            },
            {
              elevatorPitchForStartup__lower_contains:
                AFKeywordSearch.trim().toLowerCase(),
            },
          ]
        }
      })
    }

    const variables = {
      where: JSON.stringify(whereObject),
    }

    variables.sortByLastActive = AFLastActive
    variables.perPagePremium = perPagePremium
    variables.perPage = me ? perPage : perPage + perPagePremium
    variables.myId = me && me.id

    if (pageNumber > 1) {
      variables.skip = (pageNumber - 1) * perPage
      variables.skipPremium = (pageNumber - 1) * perPagePremium
    }

    return (
      <Composer
        components={[
          ({ render }) => <Query query={LOCAL_UI_QUERY}>{render}</Query>,
          ({ render }) => (
            <Mutation
              mutation={LOCAL_TOGGLE_FOUNDERS_GRID_ADDITIONAL_DETAILS_MUTATION}
              variables={{ id: "" }}
            >
              {render}
            </Mutation>
          ),
          ({ render }) => (
            <Query query={USERS_LOOKING_FOR_COFOUNDER} variables={variables}>
              {render}
            </Query>
          ),
          ({ render }) => (
            <Query
              query={PREMIUM_USERS_LOOKING_FOR_COFOUNDER}
              variables={variables}
            >
              {render}
            </Query>
          ),
          ({ render }) => (
            <Mutation
              mutation={LOCAL_TOGGLE_PREMIUM_COFOUNDER}
              variables={{ message: null }}
            >
              {render}
            </Mutation>
          ),
          ({ render }) => (
            <Query
              query={USERS_LOOKING_FOR_Vendor}
              variables={{ ...variables, skip: variables.skipPremium }}
            >
              {render}
            </Query>
          ),
        ]}
      >
        {([
          uiState,
          toggleFoundersGridAdditionalDetails,
          usersLookingForCofounder,
          premiumUsersLookingForCofounder,
          togglePremiumCofounder,
          usersLookingForVendor,
        ]) => (
          <div className="user-grid">
            <div className="user-grid__filters-button">
              <Cta
                type="button"
                tag="button"
                text="Open filters"
                onClick={toggleFounderFilters}
                bgColor="white"
              />
            </div>
            <UserGridFilters
              me={me}
              filterOverlayActive={uiState.data.ui.founderFiltersActive}
              updateValue={this.updateValue}
              toggleSelection={this.toggleSelection}
              FHasIdeaForStartup={FHasIdeaForStartup}
              FLookingToJoinStartupAsFounder={FLookingToJoinStartupAsFounder}
              FHasSkills={FHasSkills}
              FIdeaStages={FIdeaStages}
              FContacts={FContacts}
              FHoursPerWeekData={FHoursPerWeekData}
              FIndustriesData={FIndustriesData}
              FLocationByCity={FLocationByCity}
              FLocationByCountry={FLocationByCountry}
              FLastActive={FLastActive}
              FKeywordSearch={FKeywordSearch}
              toggleFiltersOpen={toggleFounderFilters}
              applyFilters={this.applyFilters}
              resetFilters={this.resetFilters}
            />
            {(usersLookingForCofounder.loading ||
              premiumUsersLookingForCofounder.loading) && (
                <div className="user-grid__loading" />
              )}

            {!usersLookingForCofounder.loading &&
              !premiumUsersLookingForCofounder.loading && (
                <div className="user-grid__inner">
                  {me &&
                    has(
                      premiumUsersLookingForCofounder,
                      "data.premiumUsersLookingForCofounder"
                    ) &&
                    has(
                      usersLookingForCofounder,
                      "data.usersLookingForCofounder"
                    ) &&
                    premiumUsersLookingForCofounder.data
                      .premiumUsersLookingForCofounder.length > 0 && (
                      <>
                        {!this.props.hidePremiumTitle && (
                          <h2 className="user-grid__recommended-title">
                            <span className="user-grid__recommended-title__inner">
                              Featured profiles
                            </span>
                          </h2>
                        )}
                        <div className="user-grid__items user-grid__items--premium">
                          {createFakeUserArray(
                            perPagePremium,
                            premiumUsersLookingForCofounder.data
                              .premiumUsersLookingForCofounder
                          ).map((index) => {
                            return typeof premiumUsersLookingForCofounder.data
                              .premiumUsersLookingForCofounder[index] ===
                              "undefined" ? (
                              <UserTilePromo
                                key={index}
                                isAlreadyPremium={
                                  me.hasValidCoFounderSubscription
                                }
                                AFLocationByCity={AFLocationByCity}
                                AFLocationByCountry={AFLocationByCountry}
                                {...me}
                                togglePremiumCofounder={togglePremiumCofounder}
                              />
                            ) : (
                              <UserTile
                                key={
                                  premiumUsersLookingForCofounder.data
                                    .premiumUsersLookingForCofounder[index].id
                                }
                                {...premiumUsersLookingForCofounder.data
                                  .premiumUsersLookingForCofounder[index]}
                                openTileId={
                                  uiState.data.ui
                                    .foundersGridAdditionalDetailsId
                                }
                                me={me}
                                signedIn={me ? true : false}
                              />
                            )
                          })}
                        </div>
                      </>
                    )}
                  {me &&
                    !me.hasValidCoFounderSubscription &&
                    has(
                      premiumUsersLookingForCofounder,
                      "data.premiumUsersLookingForCofounder"
                    ) &&
                    premiumUsersLookingForCofounder.data
                      .premiumUsersLookingForCofounder.length ===
                    perPagePremium && (
                      <div className="user-grid__featured-banner">
                        <p>Want to reach more people with your profile?</p>
                        <br />
                        <Cta
                          modifierClass="premium"
                          text="Boost profile"
                          onClick={togglePremiumCofounder}
                          type="button"
                          tag="button"
                        />
                      </div>
                    )}
                  <div className="user-grid__items user-grid__items--premium">
                    {has(
                      usersLookingForCofounder,
                      "data.usersLookingForCofounder"
                    ) &&
                      usersLookingForCofounder.data.usersLookingForCofounder.map(
                        (user) => {
                          return (
                            <>
                              <UserTile
                                key={user.id}
                                {...user}
                                openTileId={
                                  uiState.data.ui
                                    .foundersGridAdditionalDetailsId
                                }
                                me={me}
                                signedIn={me ? true : false}
                              />
                            </>
                          )
                        }
                      )}
                  </div>
                  {/* load vendors */}
                  {me && false && (
                    <>
                      {/* Add Heading */}
                      <h2 className="user-grid__recommended-title">
                        <span className="user-grid__recommended-title__inner">
                          Resources and Vendors
                        </span>
                      </h2>
                      {/* Add Heading */}
                      <div className="user-grid__items">
                        {has(
                          usersLookingForVendor,
                          "data.usersLookingForVendor"
                        ) &&
                          usersLookingForVendor.data.usersLookingForVendor.map(
                            (user) => {
                              return (
                                <VendorTile
                                  key={user.id}
                                  {...user}
                                  openTileId={
                                    uiState.data.ui
                                      .foundersGridAdditionalDetailsId
                                  }
                                  me={me}
                                  signedIn={me ? true : false}
                                />
                              )
                            }
                          )}
                      </div>
                    </>
                  )}
                  {/* load vendors end */}
                  {has(
                    usersLookingForCofounder,
                    "data.usersLookingForCofounder"
                  ) &&
                    usersLookingForCofounder.data.usersLookingForCofounder
                      .length === 0 &&
                    has(
                      premiumUsersLookingForCofounder,
                      "data.premiumUsersLookingForCofounder"
                    ) &&
                    premiumUsersLookingForCofounder.data
                      .premiumUsersLookingForCofounder.length === 0 && (
                      <div className="user-grid__no-results">
                        <p>
                          Didn't find any co-founders! Try removing some filters
                          or click the remove filters button below
                        </p>
                        <Cta
                          type="button"
                          tag="button"
                          text="Remove filters"
                          onClick={this.resetFilters}
                          bgColor="white"
                        />
                      </div>
                    )}
                  {portalNode &&
                    uiState.data.ui.foundersGridAdditionalDetailsId.length >
                    0 &&
                    has(
                      usersLookingForCofounder,
                      "data.usersLookingForCofounder"
                    ) &&
                    has(
                      premiumUsersLookingForCofounder,
                      "data.premiumUsersLookingForCofounder"
                    ) &&
                    ReactDOM.createPortal(
                      <PagePopup
                        togglePopup={toggleFoundersGridAdditionalDetails}
                        modifierClass="additional-details"
                      >
                        <AdditionalDetails
                          me={me}
                          {...usersLookingForCofounder.data.usersLookingForCofounder
                            .concat(
                              premiumUsersLookingForCofounder.data
                                .premiumUsersLookingForCofounder
                            )
                            .concat(
                              usersLookingForVendor.data.usersLookingForVendor
                            )
                            .filter(
                              (user) =>
                                user.id ===
                                uiState.data.ui.foundersGridAdditionalDetailsId
                            )[0]}
                          signupOverlayActive={signupOverlayActive}
                        />
                      </PagePopup>,
                      portalNode
                    )}
                </div>
              )}
            <Pagination
              variables={variables}
              pageNumber={pageNumber}
              query={query}
              usersPerPage={usersPerPage}
              me={me}
            />
          </div>
        )}
      </Composer>
    )
  }
}

export default UserGrid
